import { endOfMonth, endOfWeek, type Contact } from '@crmsy/shared';
import { matchesAny } from './search.js';

/** Table / Follow-ups filter state (§6.3). Lives in the URL so views are shareable. */
export interface Filters {
  stage: string[];
  nextAction: string[];
  status: string[];
  owner: string[];
  mine: boolean;
  due: boolean;
  week: boolean;
  month: boolean;
  archived: boolean;
  q: string;
}

export const EMPTY_FILTERS: Filters = {
  stage: [],
  nextAction: [],
  status: [],
  owner: [],
  mine: false,
  due: false,
  week: false,
  month: false,
  archived: false,
  q: '',
};

export type ListFilterKey = 'stage' | 'nextAction' | 'status' | 'owner';

const LIST_KEYS: Record<ListFilterKey, string> = { stage: 'stage', nextAction: 'action', status: 'status', owner: 'owner' };
const FLAG_KEYS = ['mine', 'due', 'week', 'month', 'archived'] as const;

export function filtersFromSearch(sp: URLSearchParams): Filters {
  const f: Filters = { ...EMPTY_FILTERS, stage: [], nextAction: [], status: [], owner: [] };
  for (const [k, param] of Object.entries(LIST_KEYS) as [ListFilterKey, string][]) {
    f[k] = sp.getAll(param).flatMap((v) => v.split(',')).filter(Boolean);
  }
  for (const k of FLAG_KEYS) f[k] = sp.has(k);
  f.q = sp.get('q') ?? '';
  return f;
}

export function filtersToSearch(f: Filters): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [k, param] of Object.entries(LIST_KEYS) as [ListFilterKey, string][]) {
    if (f[k].length) sp.set(param, f[k].join(','));
  }
  for (const k of FLAG_KEYS) if (f[k]) sp.set(k, '');
  if (f.q) sp.set('q', f.q);
  return sp;
}

export function filtersEmpty(f: Filters): boolean {
  return (
    !f.stage.length && !f.nextAction.length && !f.status.length && !f.owner.length &&
    !f.mine && !f.due && !f.week && !f.month && !f.archived && !f.q
  );
}

/** Due · This week · This month are one choice: turning one on turns the others off (§7). */
export function toggleWindow(f: Filters, key: 'due' | 'week' | 'month'): Filters {
  const on = !f[key];
  return { ...f, due: false, week: false, month: false, [key]: on };
}

export function toggleInList(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export interface FilterContext {
  today: string;
  myNotionId: string | null;
}

/** Apply filters (AND) and search; returns a new array sorted by search score when searching. */
export function applyFilters(contacts: Contact[], f: Filters, ctx: FilterContext): Contact[] {
  const weekEnd = endOfWeek(ctx.today);
  const monthEnd = endOfMonth(ctx.today);
  // §7: d = due today or overdue; w/m = due by end of window including overdue. One window at a
  // time (the chips are exclusive); should a URL carry several, the narrowest wins.
  const dueLimit = f.due ? ctx.today : f.week ? weekEnd : f.month ? monthEnd : null;
  const out: { c: Contact; score: number }[] = [];
  for (const c of contacts) {
    if (c.archived !== f.archived) continue;
    if (f.stage.length && !f.stage.includes(c.stage ?? '')) continue;
    if (f.status.length && !f.status.includes(c.status ?? '')) continue;
    if (f.nextAction.length && !f.nextAction.includes(c.nextAction ?? '')) continue;
    if (f.owner.length && !c.owner.some((o) => f.owner.includes(o))) continue;
    if (f.mine && (!ctx.myNotionId || !c.owner.includes(ctx.myNotionId))) continue;
    if (dueLimit && (!c.nextContactDate || c.nextContactDate > dueLimit)) continue;
    let score = 1;
    if (f.q) {
      score = matchesAny(f.q, [c.companyName, c.contact, c.email, c.phone]);
      if (!score) continue;
    }
    out.push({ c, score });
  }
  if (f.q) out.sort((a, b) => b.score - a.score);
  return out.map((o) => o.c);
}
